import React, {
  useContext,
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback
} from 'react';
import {
  Box,
  Container,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import CloseIcon from 'remixicon-react/CloseFillIcon';
import ChatIcon from 'remixicon-react/QuestionAnswerFillIcon';
import HelpIcon from 'remixicon-react/QuestionnaireFillIcon';
import SearchIcon from 'remixicon-react/Search2FillIcon';
import { AppContext } from './AppContext';
import ChatFrame from './ChatFrame';
import { useIframe } from './FrameUtils';
import { useTheme } from '../theme';

const actions = [
  { icon: <ChatIcon />, name: 'Chat' },
  { icon: <SearchIcon />, name: 'Search' }
];

function App() {
  const { state, dispatch } = useContext(AppContext);
  const [showSpeedDial, setShowSpeedDial] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    window.matchMedia('(max-width: 768px)').matches
  );

  const containerRef = useRef<HTMLDivElement | null>(null);
  const toggleRef = useRef<HTMLDivElement | null>(null);

  const { theme, config } = useTheme();
  const sendFrameMessage = useIframe(dispatch);

  const updateToggleOffset = useCallback(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const toggle = toggleRef.current;

    if (!toggle) {
      container.style.removeProperty('--toggle-offset');
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const toggleRect = toggle.getBoundingClientRect();
    const toggleButton = toggle.querySelector('button');
    const targetRect = toggleButton?.getBoundingClientRect() ?? toggleRect;
    const offset = Math.max(0, containerRect.bottom - targetRect.top);

    container.style.setProperty('--toggle-offset', `${Math.ceil(offset)}px`);
  }, []);

  // ------------------------------------------------------------------
  // Automatically show the prompt after 5s IF the user hasn't interacted.
  // We use a useEffect to avoid multiple timeouts and race conditions.
  // ------------------------------------------------------------------
  const shouldShowPromptForDevice = () => {
    if (isMobile) {
      return config?.PromptBubbleShowOnMobile !== false;
    }
    return config?.PromptBubbleShowOnDesktop !== false;
  };

  useEffect(() => {
    // Skip if user has permanently dismissed the prompt
    if (state.hasShownPrompt) {
      return;
    }

    // Skip if device config disables prompt
    if (!shouldShowPromptForDevice()) {
      return;
    }

    // Only set the timeout if all these conditions are true
    // meaning the user hasn't opened the chat or entered info, etc.
    if (!showPrompt && !state.showChat && !state.enteredInformation && !state.hasOpened) {
      const timerId = setTimeout(() => {
        // Double-check states before actually showing the prompt
        if (!showPrompt && !state.showChat && !state.enteredInformation && !state.hasOpened) {
          setShowPrompt(true);
        }
      }, 5000);

      // Cleanup if component unmounts or any dependency changes
      return () => clearTimeout(timerId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPrompt, state.showChat, state.enteredInformation, state.hasOpened, state.hasShownPrompt, isMobile, config]);

  // ------------------------------------------------------------------
  // Automatically open chat if OpenByDefault is enabled in config
  // ------------------------------------------------------------------
  useEffect(() => {
    if (config?.OpenByDefault && !state.hasOpened) {
      dispatch({ type: 'setShowChat', showChat: true });
      dispatch({ type: 'setHasOpened', hasOpened: true });
    }
  }, [config?.OpenByDefault, state.hasOpened, dispatch]);

  // ------------------------------------------------------------------
  // Listen for device size changes (mobile vs desktop)
  // ------------------------------------------------------------------
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // ------------------------------------------------------------------
  // Toggles the chat window open/closed
  // ------------------------------------------------------------------
  const toggleChat = () => {
    setShowSpeedDial(false);
    setShowPrompt(false);

    // If chat is closed, open it; if open, close it.
    dispatch({ type: 'setShowChat', showChat: !state.showChat });

    // Mark as opened so the prompt won't appear again
    dispatch({ type: 'setHasOpened', hasOpened: true });
  };

  // ------------------------------------------------------------------
  // Navigate inside the chat widget (e.g., Chat, Search)
  // ------------------------------------------------------------------
  const navigateWidget = (path: string) => {
    sendFrameMessage({ isEdulla: true, action: 'navigate', data: path });
    toggleChat();
  };

  // ------------------------------------------------------------------
  // Dismiss the prompt bubble permanently
  // ------------------------------------------------------------------
  const dismissPrompt = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowPrompt(false);
    dispatch({ type: 'setHasShownPrompt', hasShownPrompt: true });
  };

  useEffect(() => {
    if (!state.showChat) {
      return;
    }

    const timeout = window.setTimeout(() => {
      document.getElementById('edullaChatWidgetFrame')?.focus();
    }, 0);

    return () => window.clearTimeout(timeout);
  }, [state.showChat]);


  useLayoutEffect(() => {
    let frameId = requestAnimationFrame(updateToggleOffset);

    const scheduleUpdate = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(updateToggleOffset);
    };

    window.addEventListener('resize', scheduleUpdate);

    let resizeObserver: ResizeObserver | undefined;

    const observeToggleElements = () => {
      if (!resizeObserver) {
        return;
      }

      const toggleElement = toggleRef.current;
      if (!toggleElement) {
        return;
      }

      resizeObserver.observe(toggleElement);
      const toggleButton = toggleElement.querySelector('button');
      if (toggleButton) {
        resizeObserver.observe(toggleButton);
      }
    };

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(scheduleUpdate);
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }
      observeToggleElements();
    }

    let mutationObserver: MutationObserver | undefined;
    if (typeof MutationObserver !== 'undefined' && containerRef.current) {
      mutationObserver = new MutationObserver(() => {
        observeToggleElements();
        scheduleUpdate();
      });
      mutationObserver.observe(containerRef.current, {
        attributes: true,
        childList: true,
        subtree: true
      });
    }

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', scheduleUpdate);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, [updateToggleOffset]);

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  if (!theme) {
    return <h1>Loaded</h1>;
  }

  const chatButtonLabel = state.showChat ? 'Close chat' : 'Open chat';

  return (
    <ThemeProvider theme={theme}>
      {/* StrictMode is optional; it's great for dev but often removed in prod */}
      <React.StrictMode>
        <Container
          component="aside"
          role="complementary"
          aria-label="Site assistance chat"
          ref={containerRef}
          sx={{
            p: '0 !important',
            position: 'fixed',
            bottom: 25,
            right: 25,
            width: 'auto',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            alignItems: 'flex-end',
            gap: 0,
            maxHeight: '95vh',
            height: state.showChat ? '95vh' : 'auto',
            background: 'none !important',
            zIndex: 999999,
            pointerEvents: 'none',
          }}
        >
          {/* The actual iframe-based chat window */}
          <ChatFrame />

          {/* The bubble prompt (appears if user hasn't interacted after 5s) */}
          <Box
            sx={{
              fontFamily: 'sans-serif',
              position: 'absolute',
              right: 0,
              bottom: 69,
              background: '#e8e8e8',
              borderRadius: '6px',
              maxHeight: showPrompt ? '90px' : '0px',
              maxWidth: showPrompt ? '260px' : '0px',
              overflow: 'hidden',
              opacity: showPrompt ? 1 : 0,
              padding: showPrompt ? '10px 32px 10px 20px' : '0px',
              transition: 'all 0.5s',
              minWidth: '200px',
              fontSize: '16px',
              color: '#212529',
              boxShadow:
                '0px 3px 5px -1px rgba(0,0,0,0.2),' +
                '0px 6px 10px 0px rgba(0,0,0,0.14),' +
                '0px 1px 18px 0px rgba(0,0,0,0.12)',
              pointerEvents: showPrompt ? 'auto' : 'none'
            }}
            role="status"
            aria-live="polite"
          >
            <Box
              component="button"
              onClick={dismissPrompt}
              aria-label="Dismiss greeting"
              sx={{
                position: 'absolute',
                top: '4px',
                right: '4px',
                width: '20px',
                height: '20px',
                padding: 0,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                '&:hover': {
                  background: 'rgba(0,0,0,0.1)',
                },
                '&:focus': {
                  outline: '2px solid #666',
                  outlineOffset: '1px',
                }
              }}
            >
              <CloseIcon size={14} />
            </Box>
            {config?.PromptBubbleMessage || "Hello! Is there anything I can help you with today?"}
          </Box>

          {/* The floating SpeedDial for Chat/Search */}
          <SpeedDial
            ariaLabel={chatButtonLabel}
            FabProps={{
              'aria-expanded': state.showChat,
              'aria-label': chatButtonLabel
            }}
            ref={toggleRef}
            onClick={toggleChat}
            open={!state.showChat && showSpeedDial && state.enteredInformation}
            onMouseEnter={() => setShowSpeedDial(true)}
            onMouseLeave={() => setShowSpeedDial(false)}
            icon={
              <SpeedDialIcon
                icon={state.showChat ? <CloseIcon size={24} /> : <HelpIcon size={24} />}
                openIcon={<CloseIcon size={24} />}
                sx={{ svg: { width: 24, height: 24 } }}
              />
            }
          >
            {/* If the speed dial is open, show Chat and Search actions */}
            {!state.showChat &&
              showSpeedDial &&
              state.enteredInformation &&
              actions.map((action) => (
                <SpeedDialAction
                  key={action.name}
                  icon={action.icon}
                  tooltipTitle={action.name}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateWidget(`/${action.name}`);
                  }}
                />
              ))}
          </SpeedDial>
        </Container>
      </React.StrictMode>
    </ThemeProvider>
  );
}

export default App;
